import { useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "./index.css";
import dysonImg from "./assets/dyson.png";
import nomiMascot from "./assets/nomi-mascot.png";
import predictImg from "./assets/Predict-Pro.png";
import aiSendImg from "./assets/AI_send.png";
import creditCardImg from "./assets/CreditCard.png";
import fifaImg from "./assets/spidey-fifa.png";
import bubbleNavImg from "./assets/bubblenavbar.png";
import xPvc from "./assets/x-pvc.png";
import linkedinPvc from "./assets/linkedin-pvc.png";
import githubPvc from "./assets/github-pvc.png";
import resumePvc from "./assets/resume-pvc.png";
import resumePdf from "../yash1.pdf";
import avatarPvc from "./assets/avatar-pvc.png";
import heroCone from "./assets/hero-cone.webp";
import popBombSfx from "./assets/pop-bomb.mp3";
import popBalloonSfx from "./assets/pop-nomi.mp3";
import stReact from "./assets/stack/react.webp";
import stTs from "./assets/stack/typescript.webp";
import stNext from "./assets/stack/nextjs.webp";
import stGsap from "./assets/stack/gsap.webp";
import stMotion from "./assets/stack/motion.webp";
import stThree from "./assets/stack/threejs.webp";
import stTailwind from "./assets/stack/tailwind.webp";
import stNode from "./assets/stack/node.webp";
import stFigma from "./assets/stack/figma.webp";
import stRive from "./assets/stack/rive.webp";
import heroCaution from "./assets/hero-caution.webp";
import talkamoreImg from "./assets/talkamore-phones.webp"; // phones cut out of the old olive shot, shown on the site's cream
import talkamoreWebImg from "./assets/talkamore-web.jpg";
import talkamoreLogo from "./assets/talkamore-logo.png";
import kraneAppsLogo from "./assets/kraneapps-logo.png";
import superteamLogo from "./assets/superteam-logo.png";
import fensoryLogo from "./assets/fensory-logo.png";
import yashLeather from "./assets/yash-leather.png";
import character from "./assets/character.png";

const INK = "#111111";
const BLUE = "#4b63ff";

/* ---- the bomb's pop SFX ----
   Each sound is a single preloaded element retriggered via currentTime=0.
   Browsers gate audio behind a user gesture, so on the first pointer/key
   interaction we "unlock" every clip (muted play→pause) — that way the
   hover-triggered bomb can still sound. play() rejections are swallowed. */
const sfxPool: HTMLAudioElement[] = [];
function makePop(src: string, volume: number): () => void {
  let el: HTMLAudioElement | null = null;
  if (typeof Audio !== "undefined") {
    el = new Audio(src);
    el.preload = "auto";
    el.volume = volume;
    sfxPool.push(el);
  }
  return () => {
    if (!el) return;
    try {
      el.currentTime = 0;
      const p = el.play();
      if (p) p.catch(() => {});
    } catch { /* ignore */ }
  };
}
const playBombPop = makePop(popBombSfx, 0.6);

if (typeof window !== "undefined") {
  const unlockSfx = () => {
    sfxPool.forEach((a) => {
      a.muted = true;
      const p = a.play();
      const reset = () => { a.pause(); a.currentTime = 0; a.muted = false; };
      if (p) p.then(reset).catch(() => { a.muted = false; });
      else reset();
    });
    window.removeEventListener("pointerdown", unlockSfx);
    window.removeEventListener("keydown", unlockSfx);
  };
  window.addEventListener("pointerdown", unlockSfx);
  window.addEventListener("keydown", unlockSfx);
}

/* ---- balloon pop: Web Audio, so each balloon pops at its own pitch, rapid clicks
   overlap, and a deeper squeeze is louder. The context + decoded clip are prepared
   on the first gesture anywhere (browsers only allow audio after one). ---- */
let popCtx: AudioContext | null = null;
let popBuf: AudioBuffer | null = null;
function primeBalloonPop() {
  if (popCtx || typeof AudioContext === "undefined") return;
  popCtx = new AudioContext();
  fetch(popBalloonSfx).then((r) => r.arrayBuffer()).then((b) => popCtx!.decodeAudioData(b))
    .then((buf) => { popBuf = buf; }).catch(() => { /* no sound, no problem */ });
}
function playBalloonPop(rate: number, volume: number) {
  if (!popCtx || !popBuf) return;
  if (popCtx.state === "suspended") void popCtx.resume();
  const src = popCtx.createBufferSource(), gain = popCtx.createGain();
  src.buffer = popBuf;
  src.playbackRate.value = rate * (0.96 + Math.random() * 0.08); // never the exact same pop twice
  gain.gain.value = volume;
  src.connect(gain).connect(popCtx.destination);
  src.start();
}
if (typeof window !== "undefined") {
  window.addEventListener("pointerdown", primeBalloonPop, { once: true });
  window.addEventListener("keydown", primeBalloonPop, { once: true });
}

/* ================================================================== */
/*  WORDMARK — Bagel Fat One bubble letterforms rendered as an inflated */
/*  latex object via the #balloon inflate filter (tube volume from a    */
/*  blurred-alpha height field) + hand-placed gloss. Blur+composite     */
/*  only, so it's smooth at any resolution with no lighting banding.    */
/*  Each letter is its own HTML <div> so transforms are cheap GPU       */
/*  composites of a cached layer, not per-frame filter re-rasters.      */
/* ================================================================== */

const CW: Record<string, number> = { Y: 0.7, A: 0.7, S: 0.64, H: 0.72 };

/* hand-tuned gloss highlights per letter (fractions of the letter cell) */
const HI: Array<Array<{ fx: number; fy: number; fw: number; fh: number; rot: number; o: number }>> = [
  [ // Y
    { fx: 0.05, fy: 0.1, fw: 0.27, fh: 0.1, rot: 36, o: 0.95 },
    { fx: 0.46, fy: 0.07, fw: 0.24, fh: 0.09, rot: -30, o: 0.95 },
    { fx: 0.32, fy: 0.52, fw: 0.09, fh: 0.28, rot: 2, o: 0.63 },
    { fx: 0.14, fy: 0.3, fw: 0.07, fh: 0.07, rot: 0, o: 0.46 },
  ],
  [ // A
    { fx: 0.26, fy: 0.05, fw: 0.28, fh: 0.1, rot: 7, o: 0.95 },
    { fx: 0.08, fy: 0.42, fw: 0.1, fh: 0.32, rot: 13, o: 0.69 },
    { fx: 0.6, fy: 0.45, fw: 0.09, fh: 0.26, rot: -11, o: 0.52 },
    { fx: 0.49, fy: 0.22, fw: 0.07, fh: 0.07, rot: 0, o: 0.46 },
  ],
  [ // S
    { fx: 0.2, fy: 0.05, fw: 0.31, fh: 0.1, rot: -12, o: 0.95 },
    { fx: 0.25, fy: 0.6, fw: 0.28, fh: 0.1, rot: -10, o: 0.75 },
    { fx: 0.12, fy: 0.34, fw: 0.08, fh: 0.08, rot: 0, o: 0.46 },
  ],
  [ // H
    { fx: 0.07, fy: 0.08, fw: 0.11, fh: 0.34, rot: 2, o: 0.95 },
    { fx: 0.56, fy: 0.07, fw: 0.11, fh: 0.36, rot: -2, o: 0.95 },
    { fx: 0.34, fy: 0.43, fw: 0.15, fh: 0.08, rot: 1, o: 0.57 },
    { fx: 0.1, fy: 0.66, fw: 0.08, fh: 0.16, rot: 2, o: 0.4 },
  ],
];

/* per-letter character: tilt + horizontal squeeze, matched to the reference
   (Y leans right, A upright + wears the crown, S straight & squeezed, H slight tilt) */
const LETTERS = [
  { c: "Y", rot: 7, sx: 1.0, crown: false },
  { c: "A", rot: 0, sx: 1.0, crown: true },
  { c: "S", rot: -1, sx: 0.9, crown: false },
  { c: "H", rot: 4, sx: 1.0, crown: false },
];
const KERN = 0.85; // < 1 → letters overlap & touch like the reference

function GlobalDefs() {
  return (
    <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
      <defs>
        {/* dimmed soft-gray body (the "low-opacity black layer") — white reflections
            are painted on top so they pop against it, like the reference */}
        {/* A's stuffed triangular counter: sunken floor, deep/dark at top → light bounce at base */}
        <linearGradient id="counterFloor" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#454039" />
          <stop offset="0.5" stopColor="#b3ab9c" />
          <stop offset="1" stopColor="#ece8df" />
        </linearGradient>
        {/* BODY: bright (not pure-white) top-left → warm grey lower-right, so the pure-white
            speculars on top read as glossy pops against the diffuse surface */}
        <radialGradient id="latexBase" cx="0.37" cy="0.15" r="1.06">
          <stop offset="0" stopColor="#fdfcf8" />
          <stop offset="0.36" stopColor="#eeeae1" />
          <stop offset="0.68" stopColor="#d8d0c0" />
          <stop offset="1" stopColor="#c0b7a4" />
        </radialGradient>
        {/* INFLATE — crisp rounded silhouette + soft form shadow + crease + rim light */}
        <filter id="balloon" x="-30%" y="-30%" width="160%" height="160%" colorInterpolationFilters="sRGB">
          {/* 1. round corners CRISPLY: blur then steep alpha threshold (keeps a ~1px AA edge so
                the A's triangle & H joints go soft/fluffy but the silhouette stays sharp) */}
          <feGaussianBlur in="SourceGraphic" stdDeviation="7" result="gb" />
          <feColorMatrix in="gb" type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 13 -5.7" result="rGraphic" />
          <feColorMatrix in="rGraphic" type="matrix" values="0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0" result="rAlpha" />

          {/* height field (recess detection) off the rounded shape */}
          <feGaussianBlur in="rAlpha" stdDeviation="13" result="h" />
          {/* 2. FORM SHADOW — lower-right crescent, warm grey, soft (volume not darkness) */}
          <feOffset in="rAlpha" dx="-20" dy="-24" result="aUL" />
          <feComposite in="rAlpha" in2="aUL" operator="out" result="shA" />
          <feComposite in="shA" in2="rAlpha" operator="in" result="shClip" />
          <feGaussianBlur in="shClip" stdDeviation="11" result="shB" />
          <feComposite in="shB" in2="rAlpha" operator="in" result="shClip2" />
          <feFlood floodColor="#b6ac98" floodOpacity="0.62" result="shCol" />
          <feComposite in="shCol" in2="shClip2" operator="in" result="shade" />
          {/* 3. CREASE / recess — valley where lobes meet (light grey, not black) */}
          <feComponentTransfer in="h" result="coreTight">
            <feFuncA type="table" tableValues="0 0 0 0 0.1 0.5 0.85 1" />
          </feComponentTransfer>
          <feComposite in="rAlpha" in2="coreTight" operator="out" result="seamA" />
          <feComposite in="seamA" in2="rAlpha" operator="in" result="seamC" />
          <feGaussianBlur in="seamC" stdDeviation="5" result="seamB" />
          <feFlood floodColor="#9b9180" floodOpacity="0.34" result="seamCol" />
          <feComposite in="seamCol" in2="seamB" operator="in" result="seam" />
          {/* 4. RIM LIGHT — top-left edge, bright white */}
          <feOffset in="rAlpha" dx="16" dy="19" result="aDR" />
          <feComposite in="rAlpha" in2="aDR" operator="out" result="hiA" />
          <feComposite in="hiA" in2="rAlpha" operator="in" result="hiC" />
          <feGaussianBlur in="hiC" stdDeviation="5" result="hiB" />
          <feFlood floodColor="#ffffff" floodOpacity="0.95" result="hiCol" />
          <feComposite in="hiCol" in2="hiB" operator="in" result="hi" />
          <feMerge>
            <feMergeNode in="rGraphic" />
            <feMergeNode in="shade" />
            <feMergeNode in="seam" />
            <feMergeNode in="hi" />
          </feMerge>
        </filter>
        <filter id="b7" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="5" /></filter>
        <filter id="b7mid" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="2.4" /></filter>
        <filter id="b7tight" x="-60%" y="-60%" width="220%" height="220%"><feGaussianBlur stdDeviation="1.1" /></filter>

      </defs>
    </svg>
  );
}

function CrownHat() {
  const [boop, setBoop] = useState(0);
  return (
    <div className="crownHat" style={{ position: "absolute", left: "48%", top: "5%", width: "38%", transform: "translateX(-50%)", zIndex: 6, cursor: "pointer" }}
      onClick={() => setBoop((b) => b + 1)} aria-hidden="true">
      <div className={boop ? "crownBoop" : undefined} key={boop}>
        <svg viewBox="0 0 80 50" style={{ width: "100%", height: "auto", overflow: "visible" }}>
          <g className="crownSway">
            {/* 3-point crown; its base arches UP in the middle so it cups the A's rounded head */}
            <path className="drawPath" d="M11 40 L22 15 L33 29 L40 9 L47 29 L58 15 L69 40 Q40 28 11 40" stroke={BLUE} strokeWidth="3.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            <circle className="dotPop" cx="22" cy="15" r="3.3" fill={BLUE} />
            <circle className="dotPop" cx="40" cy="9" r="3.5" fill={BLUE} />
            <circle className="dotPop" cx="58" cy="15" r="3.3" fill={BLUE} />
          </g>
        </svg>
      </div>
    </div>
  );
}

function Wordmark({ charClass }: { charClass?: string }) {
  const fs = 300;
  const PAD = fs * 0.2;
  const wOf = (c: string) => (CW[c] ?? 0.68) * fs;
  const localH = fs * 1.5;
  const baseY = fs * 1.12;
  const dil = fs * 0.05;
  const font = { fontFamily: "'Bagel Fat One','Titan One',system-ui", fontSize: fs } as const;

  let cur = 0;
  const boxes = LETTERS.map((it) => {
    const ww = wOf(it.c);
    const box = { ...it, ww, left: cur, localW: ww + 2 * PAD };
    cur += ww * KERN;
    return box;
  });
  const last = boxes[boxes.length - 1]!;
  const outerW = last.left + last.localW;

  const letterDiv = (b: (typeof boxes)[number], i: number) => {
    const gx = PAD;
    const ccx = gx + b.ww / 2, ccy = baseY - fs * 0.36;
    const glyph = { x: gx, y: baseY, ...font, strokeLinejoin: "round" as const };
    const hcX = gx - fs * 0.12, hcW = b.ww + fs * 0.24, hcY = baseY - fs * 1.18, hcH = fs * 1.42;
    const tf = `rotate(${b.rot} ${ccx} ${ccy}) translate(${ccx} 0) scale(${b.sx} 1) translate(${-ccx} 0)`;
    const divStyle: React.CSSProperties = {
      position: "absolute", left: `${(b.left / outerW) * 100}%`, top: 0,
      width: `${(b.localW / outerW) * 100}%`, height: "100%", zIndex: LETTERS.length - i,
      cursor: "pointer", pointerEvents: "auto",
    };
    // the outer div never moves (stable hover/press target); .charBody does all the motion
    const bodyStyle: React.CSSProperties = {
      position: "absolute", inset: 0, transformOrigin: "center bottom",
      filter: `drop-shadow(0 0 ${fs * 0.012}px rgba(150,142,126,.45)) drop-shadow(${fs * 0.012}px ${fs * 0.03}px ${fs * 0.022}px rgba(45,40,32,.24))`,
    };
    return (
      <div key={i} className={charClass} data-ci={i} style={divStyle}>
       <div className="charBody" style={bodyStyle}>
        <svg viewBox={`0 0 ${b.localW} ${localH}`} style={{ width: "100%", height: "100%", display: "block", overflow: "visible" }}>
          <g transform={tf}>
            <g filter="url(#balloon)">
              <text {...glyph} fill="url(#latexBase)" stroke="url(#latexBase)" strokeWidth={dil}>{b.c}</text>
            </g>
            <mask id={`bm${i}`} maskUnits="userSpaceOnUse" x={-PAD} y={-PAD} width={b.localW + 2 * PAD} height={localH + 2 * PAD}>
              <text {...glyph} fill="#fff" stroke="#fff" strokeWidth={dil}>{b.c}</text>
            </mask>
            <g mask={`url(#bm${i})`} filter="url(#b7)">
              {(HI[i] ?? []).map((hl, k) => {
                const hw = hl.fw * hcW, hh = hl.fh * hcH;
                const cx = hcX + hl.fx * hcW + hw / 2, cy = hcY + hl.fy * hcH + hh / 2;
                return (
                  <rect key={k} x={hcX + hl.fx * hcW} y={hcY + hl.fy * hcH} width={hw} height={hh} rx={Math.min(hw, hh) / 2}
                    fill="#ffffff" opacity={Math.min(1, hl.o * 0.82)} transform={`rotate(${hl.rot} ${cx} ${cy})`} />
                );
              })}
            </g>
            <g mask={`url(#bm${i})`} filter="url(#b7tight)">
              {(HI[i] ?? []).filter((hl) => hl.o >= 0.6).map((hl, k) => {
                const bw = hl.fw * hcW, bh = hl.fh * hcH;
                const cx = hcX + hl.fx * hcW + bw / 2, cy = hcY + hl.fy * hcH + bh / 2;
                const cw = bw * 0.6, ch = bh * 0.5;
                return (
                  <rect key={k} x={cx - cw / 2} y={cy - ch / 2} width={cw} height={ch} rx={Math.min(cw, ch) / 2}
                    fill="#ffffff" opacity={hl.o >= 0.9 ? 1 : 0.6} transform={`rotate(${hl.rot} ${cx} ${cy})`} />
                );
              })}
            </g>
          </g>
        </svg>
        {b.crown && <CrownHat />}
       </div>
      </div>
    );
  };

  // The wordmark splits at the CENTRE OF THE NAME into two PVC door panels: "YA" (left) and
  // "SH" (right). The scroll timeline swings them open on their outer hinges.
  return (
    <div style={{ position: "relative", width: "100%", aspectRatio: `${outerW} / ${localH}`, overflow: "visible", perspective: "1700px" }} role="img" aria-label="YASH">
      <div className="doorLeft" style={{ position: "absolute", inset: 0, transformOrigin: "left center", transformStyle: "preserve-3d", pointerEvents: "none" }}>
        {boxes.slice(0, 2).map((b, i) => letterDiv(b, i))}
      </div>
      <div className="doorRight" style={{ position: "absolute", inset: 0, transformOrigin: "right center", transformStyle: "preserve-3d", pointerEvents: "none" }}>
        {boxes.slice(2).map((b, j) => letterDiv(b, j + 2))}
      </div>
    </div>
  );
}

/* ================================================================== */
/*  DOODLES                                                            */
/* ================================================================== */

function Doodle({ children, style, w, h, cls, hit = true }: {
  children: React.ReactNode; style: React.CSSProperties; w: number; h: number; cls?: string; hit?: boolean;
}) {
  return (
    <svg className={cls} width={w} height={h} viewBox={`0 0 ${w} ${h}`} fill="none" style={{ position: "absolute", ...style }} aria-hidden="true">
      {hit && <rect x="0" y="0" width={w} height={h} fill="transparent" />}
      {children}
    </svg>
  );
}

function HandUnder({ color = BLUE, dy = -4 }: { color?: string; dy?: number }) {
  return (
    <svg className="handInk" viewBox="0 0 100 10" preserveAspectRatio="none" style={{ position: "absolute", left: "-2%", bottom: dy, width: "104%", height: 8 }} aria-hidden="true">
      <path pathLength="100" d="M2 7 Q28 2.5 54 5 Q78 7 98 3.5" stroke={color} strokeWidth="3.4" fill="none" strokeLinecap="round" />
    </svg>
  );
}
const U = ({ children, color = BLUE, ink = false }: { children: React.ReactNode; color?: string; ink?: boolean }) => (
  <span style={{ position: "relative", whiteSpace: "nowrap", color: ink ? INK : color }}>{children}<HandUnder color={color} /></span>
);

/* ================================================================== */
/*  MOTION HELPERS — scroll reveal · split-word headings · magnetic     */
/* ================================================================== */

/* one shared IntersectionObserver for every element tagged `data-rv` + `ref={reveal}`.
   It stamps `data-in` once the element enters the viewport and CSS runs the
   transition. A callback ref (not a page-wide query) so elements that mount later
   (the Work toggle swaps rows) get observed too. */
let revealIO: IntersectionObserver | null = null;
const revealPending = new Set<HTMLElement>();
let revealSweepT = 0;
const revealNow = (el: HTMLElement) => { el.dataset.in = "1"; revealIO?.unobserve(el); revealPending.delete(el); };
// after scrolling settles, reveal anything at or above the fold that the observer missed
const revealSweep = () => revealPending.forEach((el) => { if (el.getBoundingClientRect().top < window.innerHeight) revealNow(el); });
const prefersReduced = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
function reveal(el: HTMLElement | null) {
  if (!el) return;
  if (typeof IntersectionObserver === "undefined" || prefersReduced()) { el.dataset.in = "1"; return; }
  if (!revealIO) {
    revealIO = new IntersectionObserver((entries) => entries.forEach((e) => {
      if (e.isIntersecting) revealNow(e.target as HTMLElement);
    }), { threshold: 0.1 });
    window.addEventListener("scroll", () => { clearTimeout(revealSweepT); revealSweepT = window.setTimeout(revealSweep, 140); }, { passive: true });
  }
  revealPending.add(el);
  revealIO.observe(el);
}
const rvDelay = (ms: number) => ({ "--rv-d": `${ms}ms` } as React.CSSProperties);

/* a heading whose words rise + un-blur in sequence. "\n" = line break. The full
   string stays in an sr-only span so screen readers read it once, unsplit. */
function Words({ text, step = 55 }: { text: string; step?: number }) {
  let k = 0;
  return (
    <span className="words" data-rv="" ref={reveal}>
      <span className="srOnly">{text.replace(/\n/g, " ")}</span>
      <span aria-hidden="true">
        {text.split("\n").map((line, li) => (
          <span key={li} className="wordsLine">
            {line.split(" ").map((w, wi) => (
              <span key={wi} className="word" style={rvDelay(k++ * step)}>{w}{" "}</span>
            ))}
          </span>
        ))}
      </span>
    </span>
  );
}

/* magnetic pull toward the cursor (fine pointers only), springs back on leave */
function useMagnet<T extends HTMLElement>(strength = 0.28) {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || prefersReduced() || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    // fresh quickTo tweens per hover: the leave tween's overwrite kills the old ones for
    // good, and a new quickTo starts from the live offset (no jump mid spring-back)
    let xTo: gsap.QuickToFunc | null = null, yTo: gsap.QuickToFunc | null = null;
    const enter = () => {
      gsap.killTweensOf(el);
      xTo = gsap.quickTo(el, "x", { duration: 0.5, ease: "power3.out" });
      yTo = gsap.quickTo(el, "y", { duration: 0.5, ease: "power3.out" });
    };
    const move = (e: PointerEvent) => {
      if (!xTo || !yTo) enter();
      const r = el.getBoundingClientRect();
      xTo!((e.clientX - (r.left + r.width / 2)) * strength);
      yTo!((e.clientY - (r.top + r.height / 2)) * strength);
    };
    const leave = () => { xTo = yTo = null; gsap.to(el, { x: 0, y: 0, duration: 0.7, ease: "elastic.out(1, 0.4)", overwrite: true }); };
    el.addEventListener("pointermove", move);
    el.addEventListener("pointerenter", enter);
    el.addEventListener("pointerleave", leave);
    return () => {
      el.removeEventListener("pointerenter", enter);
      el.removeEventListener("pointermove", move);
      el.removeEventListener("pointerleave", leave);
    };
  }, [strength]);
  return ref;
}

/* ================================================================== */
/*  BALLOON PHYSICS — real damped springs instead of keyframes, so a    */
/*  squeeze is interruptible and the rebound carries the energy you put  */
/*  in. One shared rAF loop that sleeps whenever everything is at rest.  */
/* ================================================================== */
type Spring = { x: number; v: number; to: number; k: number; d: number };
const spring = (x = 0, k = 400, d = 20): Spring => ({ x, v: 0, to: x, k, d });
const tune = (s: Spring, to: number, k: number, d: number) => { s.to = to; s.k = k; s.d = d; };
function stepSpring(s: Spring, dt: number) {
  // two semi-implicit Euler substeps: stable for every stiffness used here
  for (let i = 0; i < 2; i++) {
    s.v += (-s.k * (s.x - s.to) - s.d * s.v) * (dt / 2);
    s.x += s.v * (dt / 2);
  }
}
const atRest = (...ss: Spring[]) => ss.every((s) => Math.abs(s.x - s.to) < 5e-4 && Math.abs(s.v) < 5e-3);
const snap = (...ss: Spring[]) => ss.forEach((s) => { s.x = s.to; s.v = 0; });

type Mover = (dt: number) => boolean; // returns false once settled → leaves the loop
const movers = new Set<Mover>();
let physRaf = 0, physLast = 0;
function physTick(now: number) {
  const dt = Math.min((now - physLast) / 1000, 1 / 30);
  physLast = now;
  movers.forEach((m) => { if (!m(dt)) movers.delete(m); });
  physRaf = movers.size ? requestAnimationFrame(physTick) : 0;
}
function wake(m: Mover) {
  movers.add(m);
  if (!physRaf) { physLast = performance.now(); physRaf = requestAnimationFrame(physTick); }
}

/* SQUISH — any [data-squish] object (value = strength, default 1) behaves like a small
   balloon: hover breathes it in a touch, pressing flattens it onto its base and tilts
   it toward your finger, holding keeps squeezing deeper, and letting go springs it back
   PAST round (a tall wobble) before it settles. [data-squish-host] forwards presses on
   a whole link/button to its [data-squish] child. Writes only CSS vars, read by `scale`
   and `rotate` in index.css, so it composes with any transform (GSAP, hover, magnet). */
type Squish = { el: HTMLElement; p: Spring; h: Spring; ox: number; amp: number; hold: number; m: Mover };
const squishes = new WeakMap<HTMLElement, Squish>();
function squishOf(el: HTMLElement): Squish {
  let s = squishes.get(el);
  if (!s) {
    const sq: Squish = { el, p: spring(), h: spring(), ox: 0.5, amp: parseFloat(el.dataset.squish || "") || 1, hold: 0, m: () => false };
    sq.m = (dt) => {
      stepSpring(sq.p, dt); stepSpring(sq.h, dt);
      const p = sq.p.x * sq.amp, h = sq.h.x;
      sq.el.style.setProperty("--sq-x", (1 + 0.022 * h + 0.07 * p).toFixed(4));
      sq.el.style.setProperty("--sq-y", (1 + 0.022 * h - 0.1 * p).toFixed(4));
      sq.el.style.setProperty("--sq-r", `${((sq.ox - 0.5) * 7 * p).toFixed(3)}deg`);
      return !atRest(sq.p, sq.h);
    };
    squishes.set(el, sq);
    s = sq;
  }
  return s;
}
function initSquish() {
  if (prefersReduced()) return () => {};
  let held: Squish | null = null;
  const hostOf = (t: EventTarget | null) => (t instanceof Element ? t.closest<HTMLElement>("[data-squish], [data-squish-host]") : null);
  const targetOf = (host: HTMLElement | null) => (host?.hasAttribute("data-squish") ? host : host?.querySelector<HTMLElement>("[data-squish]") ?? null);
  const release = () => {
    if (!held) return;
    clearTimeout(held.hold);
    if (held.p.x > 1.1) navigator.vibrate?.(8); // a squeeze you held, not every tap
    tune(held.p, 0, 520, 11); // underdamped (ζ≈0.24): overshoots into a stretch, wobbles twice, rests
    wake(held.m);
    held = null;
  };
  const down = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const el = targetOf(hostOf(e.target));
    if (!el) return;
    release();
    const s = squishOf(el), r = el.getBoundingClientRect();
    s.ox = Math.min(Math.max((e.clientX - r.left) / (r.width || 1), 0), 1);
    tune(s.p, 1, 900, 38); // quick, firm press
    s.hold = window.setTimeout(() => tune(s.p, 1.55, 60, 15), 170); // keep holding → keep squeezing
    held = s;
    wake(s.m);
  };
  const over = (e: PointerEvent) => {
    if (e.pointerType !== "mouse") return;
    const host = hostOf(e.target), el = targetOf(host);
    if (!host || !el || host.contains(e.relatedTarget as Node | null)) return;
    const s = squishOf(el);
    tune(s.h, 1, 260, 18);
    wake(s.m);
  };
  const out = (e: PointerEvent) => {
    const host = hostOf(e.target), el = targetOf(host);
    if (!host || !el || host.contains(e.relatedTarget as Node | null)) return;
    const s = squishOf(el);
    tune(s.h, 0, 260, 18); // (a held squeeze is released by pointerup, not by leaving)
    wake(s.m);
  };
  const key = (e: KeyboardEvent) => {
    if (e.repeat || (e.key !== "Enter" && e.key !== " ")) return;
    const el = targetOf(hostOf(e.target));
    if (!el) return;
    const s = squishOf(el);
    s.ox = 0.5;
    s.p.v += 16; // a tap's worth of impulse
    tune(s.p, 0, 520, 11);
    wake(s.m);
  };
  document.addEventListener("pointerdown", down);
  document.addEventListener("pointerup", release);
  document.addEventListener("pointercancel", release);
  document.addEventListener("pointerover", over);
  document.addEventListener("pointerout", out);
  document.addEventListener("keydown", key);
  return () => {
    document.removeEventListener("pointerdown", down);
    document.removeEventListener("pointerup", release);
    document.removeEventListener("pointercancel", release);
    document.removeEventListener("pointerover", over);
    document.removeEventListener("pointerout", out);
    document.removeEventListener("keydown", key);
  };
}

/* GLOSS — on [data-gloss] PVC objects the specular highlight slides toward the cursor,
   like a light catching plastic. Sets two % vars; the ::after paints (index.css). */
function initGloss() {
  const move = (e: PointerEvent) => {
    if (e.pointerType !== "mouse" || !(e.target instanceof Element)) return;
    const el = e.target.closest<HTMLElement>("[data-gloss]");
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--gx", `${(((e.clientX - r.left) / r.width) * 100).toFixed(1)}%`);
    el.style.setProperty("--gy", `${(((e.clientY - r.top) / r.height) * 100).toFixed(1)}%`);
  };
  document.addEventListener("pointermove", move, { passive: true });
  return () => document.removeEventListener("pointermove", move);
}

/* numbered black-blob section badge (the reference's 01 … 05 markers) */
const BLOB_BUMPS = Array.from({ length: 9 }, (_, i) => {
  const a = (i / 9) * Math.PI * 2;
  return [32 + Math.cos(a) * 20, 32 + Math.sin(a) * 20] as const;
});
function SecTag({ n, label }: { n: number; label: string }) {
  return (
    <div className="secTag" data-rv="" ref={reveal}>
      <span className="secNum" aria-hidden="true" data-squish="">
        <svg viewBox="0 0 64 64">
          <circle cx="32" cy="32" r="22" fill={INK} />
          {BLOB_BUMPS.map(([x, y], i) => <circle key={i} cx={x} cy={y} r="10.5" fill={INK} />)}
        </svg>
        <span>{String(n).padStart(2, "0")}</span>
      </span>
      <span className="secLabel">{label}<HandUnder dy={-7} /></span>
    </div>
  );
}

/* the role under the About headline swaps between Yash's three hats, on a loop.
   All three sit stacked in one inline-grid cell, so the slot stays the width of the
   longest title and the trailing copy never reflows — only the active one fades in. */
const ROLE_SWAP = ["design engineer", "mobile developer", "frontend developer"];
function RoleSwap() {
  const [i, setI] = useState(0);
  const wordRef = useRef<HTMLSpanElement>(null);
  const [w, setW] = useState<number>();
  useEffect(() => {
    const t = window.setInterval(() => setI((v) => (v + 1) % ROLE_SWAP.length), 2600);
    return () => window.clearInterval(t);
  }, []);
  // after each new title paints, measure it and set the slot width — the CSS width
  // transition then slides the trailing copy ("who …") smoothly to its new spot.
  useEffect(() => {
    if (wordRef.current) setW(wordRef.current.getBoundingClientRect().width);
  }, [i]);
  return (
    <span className="aboutRole" style={{ width: w != null ? `${w}px` : undefined }}>
      <span key={i} ref={wordRef} className="aboutRoleWord">{ROLE_SWAP[i]}<HandUnder /></span>
    </span>
  );
}

function starPath(cx: number, cy: number, outer: number, inner: number, points: number) {
  const parts: string[] = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? outer : inner;
    const a = (i / (points * 2)) * Math.PI * 2 - Math.PI / 2;
    parts.push(`${i === 0 ? "M" : "L"} ${(cx + r * Math.cos(a)).toFixed(1)} ${(cy + r * Math.sin(a)).toFixed(1)}`);
  }
  return parts.join(" ") + " Z";
}

/* bomb spots: the flanks, clear of the caution sign (top-left), the crown and doodles */
const BOMB_SPOTS: React.CSSProperties[] = [
  { right: "-10%", top: "0%" },
  { right: "-13%", top: "34%" },
  { left: "-10%", bottom: "12%" },
  { right: "-10%", bottom: "16%" },
];
const randNext = (len: number, not: number) => {
  let n = Math.floor(Math.random() * len);
  if (n === not) n = (n + 1) % len;
  return n;
};

const FlankMark = ({ side }: { side: "l" | "r" }) => (
  <svg width="26" height="22" viewBox="0 0 26 22" fill="none" aria-hidden="true" style={side === "r" ? { transform: "scaleX(-1)" } : undefined}>
    <path d="M4 4 L15 9" stroke={BLUE} strokeWidth="2.6" strokeLinecap="round" />
    <path d="M1 12 L14 12" stroke={BLUE} strokeWidth="2.6" strokeLinecap="round" />
    <path d="M4 20 L15 15" stroke={BLUE} strokeWidth="2.6" strokeLinecap="round" />
  </svg>
);

/* ================================================================== */
/*  FEATURED WORK — the single dominant project you arrive inside the   */
/*  A. One project, cinematic media, Apple/Linear/Stripe presentation.  */
/* ================================================================== */

function ArrowIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7" /><polyline points="7 7 17 7 17 17" /></svg>
  );
}

function FeaturedWork({ cinematic = false }: { cinematic?: boolean }) {
  const body = (
    <>
      <div className="dpTop">
        <span className="dpBrand">YASH<span style={{ color: BLUE }}>.</span></span>
        <span className="dpFeatLabel">FEATURED WORK — 01 / 01</span>
      </div>

      {/* one dominant project — the glimpse seen through the A.
          A static image (not video) so the browser can cache it and the growing
          clip-path stays a cheap GPU re-clip instead of a per-frame repaint. */}
      <div className="fwMedia">
        <img className="fwVideo" src={dysonImg} alt="Dyson Landing Page" />
        <div className="fwScrim" />
        <div className="fwText">
          <div className="fwEyebrow">FEATURED PROJECT</div>
          <h2 className="fwTitle">Dyson Landing Page</h2>
          <div className="fwMetaRow">
            <div className="fwMeta"><div className="k">ROLE</div><div className="v">Design &amp; Frontend</div></div>
            <div className="fwMeta"><div className="k">STACK</div><div className="v">React · GSAP · Three.js</div></div>
            <div className="fwMeta"><div className="k">YEAR</div><div className="v">2025</div></div>
          </div>
          <p className="fwDesc">An immersive product landing page — scroll-driven 3D, fluid motion and tactile micro-interactions that make the hardware feel alive.</p>
          <a className="fwCta" href="https://dyson.yashb.xyz/" target="_blank" rel="noopener noreferrer">View Live Project <ArrowIcon /></a>
        </div>
      </div>

      <div className="fwIndex">SELECTED&nbsp;WORK</div>
    </>
  );
  return <div className="darkProjects">{cinematic ? <div className="projInner">{body}</div> : body}</div>;
}

/* (RevealHero removed — it was dead code carrying a duplicate navbar; the single
   source of truth for navigation is now <SiteNav> + <QuickMenu> below.) */

/* ================================================================== */
/*  PROJECTS DATA — the /work (Projects) page is split into Mobile App   */
/*  and Website (Projects + Components) via a toggle. Same PVC universe.  */
/* ================================================================== */
type Proj = { t: string; status?: string; live?: boolean; d: string; stack?: string[]; img?: string; l?: string; fit?: "cover" | "contain" };

const MOBILE_APPS: Proj[] = [
  {
    t: "Talkamore App", status: "Live on Apple Store", live: true,
    d: "An AI journaling companion built with React Native and Xcode. Chat with a personal AI that remembers your past entries, surfaces throwbacks, and turns daily conversations into a private, encrypted journal. Features streaming chat, Auth0 sign-in, paywalled premium tiers, and paper-journal scanning.",
    stack: ["React Native", "Xcode", "TypeScript", "TanStack Query", "SuperMemory", "Auth0", "RevenueCat", "PostgreSQL"],
    img: talkamoreImg, fit: "contain", l: "https://talkamore.com",
  },
  {
    t: "NOMI", status: "Case study", live: false,
    d: "A Tamagotchi-style virtual pet built for Solana Mobile. Users mint their pet as an on-chain NFT and care for it through streaks, mini-games, and a Claude-written diary that reflects their play. Built in React Native + Expo, Three.js for the 3D pet renderer, Zustand for local state, and Cloudflare Workers (D1 + KV) as a server-side ledger sitting on top of chain memos. Shipped to the Solana dApp Store.",
    stack: ["React Native", "Expo", "Three.js", "Zustand", "Cloudflare Workers", "Solana"],
    img: nomiMascot, fit: "contain", l: "https://x.com/Yassshhu/status/2029313865331425700",
  },
  /* hidden for now — re-enable once the real write-up + stack are ready
  {
    t: "Suspect", status: "In development", live: false,
    d: "A fast-paced social-deduction party game. (Send me the real description + stack and I'll update it.)",
    stack: ["React Native", "TypeScript"],
    l: "",
  },
  */
];
const WEB_PROJECTS: Proj[] = [
  {
    t: "Talkamore", status: "Live", live: true,
    d: "The marketing & landing site for the Talkamore app: motion-led storytelling with a clean conversion flow.",
    stack: ["React", "GSAP", "Tailwind"],
    img: talkamoreWebImg, l: "https://talkamore.com",
  },
  {
    t: "PredictPro", status: "Case study", live: false,
    d: "A real-time aggregator that pulls prediction markets from Polymarket and Kalshi into one unified feed. Filter by venue, sort by volume, liquidity or price, and view live probability charts for any market.",
    stack: ["Next.js", "TypeScript", "WebSockets", "Recharts"],
    img: predictImg, l: "https://predictpro.yashb.xyz/",
  },
];
const WEB_COMPONENTS: Proj[] = [
  { t: "AI Send Button", d: "Animated send-button micro-interaction.", stack: ["React", "Framer Motion"], img: aiSendImg, l: "https://buttonai.yashb.xyz/" },
  { t: "Credit Card UI", d: "Interactive, tactile credit-card component.", stack: ["React", "CSS"], img: creditCardImg, l: "https://creditcard.yashb.xyz/" },
  { t: "FIFA Card", d: "Player card with tilt & holographic shine.", stack: ["React", "CSS 3D"], img: fifaImg, l: "https://spidey-fifa-cards.yashb.xyz/" },
  { t: "Bubble Navbar", d: "Playful springy bubble navigation bar.", stack: ["React", "Framer Motion"], img: bubbleNavImg, l: "https://bubblenavbar.yashb.xyz/" },
];
/* the hero's fanned polaroids: every project that has a shot, tagged with its tab */
const HERO_FAN = [
  ...MOBILE_APPS.map((p) => ({ ...p, tab: "mobile" as const })),
  ...WEB_PROJECTS.map((p) => ({ ...p, tab: "web" as const })),
].filter((p) => p.img);
const showWork = (tab: "mobile" | "web") => window.dispatchEvent(new CustomEvent("worktab", { detail: tab }));

/* ================================================================== */
/*  NAVIGATION — a calm 2-destination bar (Work · About) + a ⌘K quick   */
/*  menu that carries the full depth, so the bar stays minimal while     */
/*  every destination is one click away. Same paper/ink/blue universe.   */
/* ================================================================== */

type SectionId = "home" | "about" | "experience" | "work" | "contact";

/* one page, four visible destinations — every section is a click or a short scroll away */
const NAV_ITEMS: Array<{ id: SectionId; label: string }> = [
  { id: "work", label: "Work" },
  { id: "about", label: "About" },
  { id: "experience", label: "Experience" },
  { id: "contact", label: "Contact" },
];
const ABOUT_PARTS = ["Story", "Experience", "Tech Stack"];
const SOCIALS: Array<{ label: string; href: string; ext?: boolean; img?: string }> = [
  { label: "Résumé", href: resumePdf, ext: true, img: resumePvc },
  { label: "Email", href: "mailto:bharadwaj465@gmail.com" }, // same address as EMAIL (ContactSection)
  { label: "LinkedIn", href: "https://www.linkedin.com/in/yash-bharadawaj-387a67244/", ext: true, img: linkedinPvc },
  { label: "GitHub", href: "https://github.com/yash2k26", ext: true, img: githubPvc },
  { label: "Twitter", href: "https://x.com/Yassshhu", ext: true, img: xPvc },
];

/* hand-drawn divider mark between the two nav items — a tiny blue spark */
function NavDivider() {
  return (
    <svg className="navDivider" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M9 1.5 C9.6 6 12 8.4 16.5 9 C12 9.6 9.6 12 9 16.5 C8.4 12 6 9.6 1.5 9 C6 8.4 8.4 6 9 1.5 Z" fill={BLUE} />
    </svg>
  );
}

/* keyboard hint glyph (⌘K / Ctrl K) */
function KbdHint() {
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || "");
  return <span className="kbdHint"><kbd>{isMac ? "⌘" : "Ctrl"}</kbd><kbd>K</kbd></span>;
}

function SiteNav({ active, scrolled, onNavigate, onOpenMenu }: {
  active: SectionId; scrolled: boolean;
  onNavigate: (id: SectionId) => void; onOpenMenu: () => void;
}) {
  // one shared hand-drawn underline that SLIDES to the active link (instead of each
  // link toggling its own), re-measured on resize and when the nav shrinks
  // "caterpillar" motion: the edge in the travel direction leads, the tail follows.
  // Edges are CSS vars (left/right insets); data-dir picks which edge is quicker.
  const centerRef = useRef<HTMLDivElement>(null);
  const prevL = useRef<number | null>(null);
  const talkRef = useMagnet<HTMLAnchorElement>(0.22);
  useLayoutEffect(() => {
    const c = centerRef.current;
    if (!c) return;
    const measure = () => {
      const a = c.querySelector<HTMLElement>(`[data-id="${active}"]`);
      if (!a) { c.style.setProperty("--ink-o", "0"); prevL.current = null; return; }
      const l = a.offsetLeft, r = c.clientWidth - (a.offsetLeft + a.offsetWidth);
      const p = prevL.current;
      c.dataset.dir = p == null ? "none" : l > p + 1 ? "r" : l < p - 1 ? "l" : (c.dataset.dir ?? "none");
      prevL.current = l;
      c.style.setProperty("--l", `${l}px`); c.style.setProperty("--r", `${r}px`); c.style.setProperty("--ink-o", "1");
    };
    measure();
    const ro = new ResizeObserver(measure); // nav shrink on scroll + webfont swap change link widths
    c.querySelectorAll(".navlink").forEach((n) => ro.observe(n));
    return () => ro.disconnect();
  }, [active]);
  return (
    <nav className={`stageNav${scrolled ? " shrunk" : ""}`} aria-label="Primary">
      <div className="navInner">
        {/* left — logo / home */}
        <a className="navBrand brandPull" href="#top" aria-label="Yash, home"
          onClick={(e) => { e.preventDefault(); onNavigate("home"); }}>
          <span className="brandWin">
            <span className="brandReel">
              <img className="brandFace" src={yashLeather} alt="YASH" draggable={false} />
              <img className="brandFace" src={character} alt="" aria-hidden="true" draggable={false} />
              <img className="brandFace" src={yashLeather} alt="" aria-hidden="true" draggable={false} />
            </span>
          </span>
        </a>

        {/* centre — every destination, visible (desktop). active = where you are now */}
        <div className="navCenter hideMd" ref={centerRef}>
          <span className="navInk" aria-hidden="true" />
          {NAV_ITEMS.map((it) => (
            <a
              key={it.id}
              data-id={it.id}
              aria-current={active === it.id ? "true" : undefined}
              className={`navlink${active === it.id ? " active" : ""}`}
              data-squish="0.4"
              href={`#${it.id}`}
              onClick={(e) => { e.preventDefault(); onNavigate(it.id); }}
            >
              {it.label}
            </a>
          ))}
        </div>

        {/* right — primary CTA (desktop) · hamburger → full-screen menu (mobile) */}
        <div className="navRight">
          <a className="navTalk hideMd" ref={talkRef} data-squish="0.8" href="#contact" onClick={(e) => { e.preventDefault(); onNavigate("contact"); }}>
            Let&apos;s talk
          </a>
          <button className="navHamburger showMd" data-squish="" data-gloss="" onClick={onOpenMenu} aria-label="Open menu" aria-haspopup="dialog">
            <svg width="22" height="14" viewBox="0 0 22 14" fill="none" stroke={INK} strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M1 1.5h20M1 7h20M1 12.5h20" /></svg>
          </button>
        </div>
      </div>
    </nav>
  );
}

/* full-screen calm cream overlay — every destination one click away */
function QuickMenu({ open, active, onClose, onNavigate }: {
  open: boolean; active: SectionId;
  onClose: () => void; onNavigate: (id: SectionId) => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // focus the panel when it opens (so Tab cycles inside) and restore on close
  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  return (
    <div
      className={`quickMenu${open ? " open" : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Site menu"
      aria-hidden={!open}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* corner doodles — same hand-drawn universe */}
      <svg className="qmSpark qmSpark1" width="40" height="40" viewBox="0 0 40 40" fill="none" aria-hidden="true">
        <path d="M20 3 C21.6 14 26 18.4 37 20 C26 21.6 21.6 26 20 37 C18.4 26 14 21.6 3 20 C14 18.4 18.4 14 20 3 Z" stroke={BLUE} strokeWidth="2.6" />
      </svg>
      <svg className="qmSpark qmSpark2" width="30" height="30" viewBox="0 0 30 30" fill="none" aria-hidden="true">
        <path d="M15 2 C16 10 20 14 28 15 C20 16 16 20 15 28 C14 20 10 16 2 15 C10 14 14 10 15 2 Z" fill={BLUE} />
      </svg>

      <button className="qmClose" onClick={onClose} aria-label="Close menu">
        Close <span className="qmEsc">Esc</span>
      </button>

      <div className="qmInner" ref={panelRef} tabIndex={-1}>
        <div className="qmCol qmPrimary">
          <div className="qmKicker"><FlankMark side="l" /> NAVIGATE</div>
          {NAV_ITEMS.map((it, i) => (
            <button key={it.id} className={`qmBig${active === it.id ? " on" : ""}`} onClick={() => onNavigate(it.id)}>
              <span className="qmIdx">{String(i + 1).padStart(2, "0")}</span>{it.label}<i className="qmArrow" />
            </button>
          ))}
        </div>

        <div className="qmCol qmSecondary">
          <div className="qmKicker"><FlankMark side="l" /> ELSEWHERE</div>
          {SOCIALS.map((s) => (
            <a className="qmLink" key={s.label} href={s.href}
              target={s.ext ? "_blank" : undefined} rel={s.ext ? "noopener noreferrer" : undefined}
              onClick={onClose}>
              {s.label}<i className="qmArrow" />
            </a>
          ))}
          <p className="qmNote">Press <kbd>⌘</kbd><kbd>K</kbd> anytime to open this menu.</p>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  IN-PAGE SECTIONS (stubs) — real jump targets for WORK ▸ ABOUT ▸     */
/*  CONTACT so the nav/menu links actually land somewhere. Same paper   */
/*  universe; swap the inner copy for the real editorial build.         */
/* ================================================================== */

const EXPERIENCE = [
  {
    company: "Fensory", logo: fensoryLogo, location: "Remote",
    role: "Design Engineer", date: "Jul 2026 to Present",
    points: [
      "Designing and building the Fensory trading terminal: agent studio, strategy builder and live charts.",
      "Redesigned auth and onboarding end to end, from the artwork frame to the OTP animation.",
      "Rebuilt the landing page, its motion and its small-screen layouts.",
    ],
  },
  {
    company: "Talkamore", logo: talkamoreLogo, location: "Remote",
    role: "Founding Engineer", date: "Apr 2026 to Jul 2026",
    points: [
      "Built and designed the product end-to-end across web and mobile for both App Store and Play Store.",
      "Owned the core backend and system design, with a focus on scalability and real-time features.",
      "Shipped production features in a fast-moving early-stage team.",
    ],
  },
  {
    company: "KraneApps", logo: kraneAppsLogo, location: "Remote",
    role: "Design Engineer", date: "Sep 2025 to Apr 2026",
    points: [
      "Designed and shipped polished, production-ready interfaces across client web and mobile apps.",
      "Built reusable component systems and motion-led interactions used across projects.",
      "Partnered with founders to turn rough ideas into fast, tactile product experiences.",
    ],
  },
  {
    company: "Superteam", logo: superteamLogo, location: "India",
    role: "Member", date: "May 2025 to Present",
    points: [
      "Building and shipping in the Solana ecosystem with the Superteam community.",
      "Hackathons, bounties and collaborative product builds.",
    ],
  },
];

/* the job-history list — shared by the About section and the Work page lead */
function ExperienceList() {
  return (
    <ul className="expList">
      {EXPERIENCE.map((e, i) => (
        <li className="expRow" key={e.company} data-squish-host="" data-rv="" ref={reveal} style={rvDelay(i * 90)}>
          <div className="expLeft">
            <span className="expLogoTile" data-squish="" data-gloss=""><img src={e.logo} alt={`${e.company} logo`} loading="lazy" /></span>
            <div>
              <div className="expCompany">{e.company}</div>
              <div className="expLoc">{e.location}</div>
            </div>
          </div>
          <div className="expRight">
            <div className="expHead">
              <span className="expRole">{e.role}</span>
              <span className="expDate">{e.date}</span>
            </div>
            <ul className="expPoints">
              {e.points.map((pt, i) => (
                <li key={i}><span className="expDash"><ChipMark /></span><span>{pt}</span></li>
              ))}
            </ul>
          </div>
        </li>
      ))}
    </ul>
  );
}

/* the stack, from what the projects + work above were actually built with.
   Row 1: inflated PVC logos. Row 2: everything else, as PVC pills. */
const STACK_LOGOS: Array<{ name: string; img: string }> = [
  { name: "React", img: stReact }, { name: "TypeScript", img: stTs }, { name: "Next.js", img: stNext },
  { name: "GSAP", img: stGsap }, { name: "Motion", img: stMotion }, { name: "Three.js", img: stThree },
  { name: "Tailwind", img: stTailwind }, { name: "Node", img: stNode }, { name: "Figma", img: stFigma },
  { name: "Rive", img: stRive },
];
const STACK_MORE = [
  "React Native", "Expo", "JavaScript", "HTML & CSS", "Xcode", "Zustand", "TanStack Query", "Framer",
  "Cloudflare Workers", "PostgreSQL", "Solana", "WebSockets", "Recharts", "Auth0", "RevenueCat",
  "Supermemory", "Bun", "Vercel", "Git",
];
const STACK_COUNT = STACK_LOGOS.length + STACK_MORE.length;

/* ---- LIVE GitHub contributions — pulls the last year's calendar from a public
   proxy (no token) and paints it in the site's blue. Hides itself if the fetch
   fails, so a flaky API never leaves a broken card. ---- */
const GH_USER = "yash2k26";
const GH_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const GH_DAYS = ["", "Mon", "", "Wed", "", "Fri", ""];
type GhDay = { date: string; count: number; level: number };

function GitHubContributions() {
  const [days, setDays] = useState<GhDay[] | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    fetch(`https://github-contributions-api.jogruber.de/v4/${GH_USER}?y=last`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("bad status"))))
      .then((d: { total?: { lastYear?: number }; contributions?: GhDay[] }) => {
        if (!alive) return;
        setDays(d.contributions ?? []);
        setTotal(d.total?.lastYear ?? null);
      })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, []);

  // the calendar is wider than the card on phones — default the scroll to the
  // far right so the LATEST contributions are in view (no manual scrolling).
  const cardRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = cardRef.current;
    if (el && days) el.scrollLeft = el.scrollWidth;
  }, [days]);

  if (failed) return null;

  // group days into week columns (Sun→Sat); pad the first column so weekdays line up
  const weeks: (GhDay | null)[][] = [];
  if (days && days.length) {
    const firstDow = new Date(days[0]!.date + "T00:00:00Z").getUTCDay();
    const cells: (GhDay | null)[] = [...Array(firstDow).fill(null), ...days];
    for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  }
  // month label sits on the first column a new month appears in
  let lastMonth = -1;
  const monthFor = (col: (GhDay | null)[]) => {
    const d = col.find(Boolean) as GhDay | undefined;
    if (!d) return "";
    const m = new Date(d.date + "T00:00:00Z").getUTCMonth();
    if (m !== lastMonth) { lastMonth = m; return GH_MONTHS[m]!; }
    return "";
  };

  return (
    <div className="ghCal" data-part="GitHub" data-rv="" ref={reveal}>
      <span className="ghEyebrow">GitHub</span>
      <div className="ghCard" ref={cardRef}>
        {!days ? (
          <div className="ghLoading">Loading contributions…</div>
        ) : (
          <>
            <div className="ghMonths">
              {weeks.map((col, i) => <span className="ghMonthSlot" key={i}>{monthFor(col)}</span>)}
            </div>
            <div className="ghBody">
              <div className="ghDayCol">{GH_DAYS.map((d, i) => <span className="ghDayLabel" key={i}>{d}</span>)}</div>
              <div className="ghGrid">
                {weeks.map((col, i) => (
                  <div className="ghWeek" key={i}>
                    {Array.from({ length: 7 }, (_, r) => {
                      const day = col[r];
                      return (
                        <span
                          className="ghCell"
                          key={r}
                          data-level={day ? day.level : -1}
                          title={day ? `${day.count} contribution${day.count === 1 ? "" : "s"} on ${day.date}` : undefined}
                        />
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
            <div className="ghFoot">
              <span className="ghTotal">{(total ?? 0).toLocaleString()} contributions in the last year</span>
              <span className="ghLegend">
                Less
                {[0, 1, 2, 3, 4].map((l) => <span className="ghCell" key={l} data-level={l} />)}
                More
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function AboutSection() {
  return (
    <section id="about" className="aboutSection" aria-label="About">
      <div className="aboutInner container">
        {/* intro — story on the left, portrait on the right */}
        <SecTag n={2} label="About me" />
        <div className="aboutIntro">
          <div className="aboutStory" data-part="Story" data-rv="" ref={reveal}>
            <h2 className="aboutHead">
              I'm Yash, a <RoleSwap /> who builds playful, tactile interfaces that feel alive.
            </h2>
            <p className="aboutCopy">
              I'm happiest where design meets code. I care how a thing
              {" "}<U ink>feels</U> as much as how it looks: the weight of a tap, the spring of a
              transition, the small touches you notice without knowing why.
            </p>
            <a className="aboutResume" href={resumePdf} target="_blank" rel="noopener noreferrer">
              Download résumé <ArrowIcon />
            </a>
          </div>
          <div className="aboutPortrait rvPop" aria-hidden="true" data-rv="" ref={reveal} style={rvDelay(120)}>
            <img src={avatarPvc} alt="" loading="lazy" data-squish="0.35" />
            <span className="aboutPortraitTag">that's me
              <svg className="tagArrow handInk" width="22" height="20" viewBox="0 0 22 20" fill="none" aria-hidden="true">
                <path pathLength="100" d="M20 17 C13 16 6 13 3 4" stroke={BLUE} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                <path pathLength="100" d="M3 4 L9 6 M3 4 L4 10" stroke={BLUE} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          </div>
        </div>

        {/* live GitHub contributions, painted in the site blue */}
        <GitHubContributions />

        {/* Tech stack (Experience now lives in its own section below) */}
        <div className="aboutBlock aboutTech" data-part="Tech Stack">
          <h3 className="aboutSub" data-rv="" ref={reveal}>Tech stack <span className="stackCount">{STACK_COUNT} tools</span></h3>
          <div className="stackMarquee" data-rv="" ref={reveal}>
            <div className="stackRow">
              <div className="stackTrack">
                {[0, 1].map((copy) => (
                  <ul className="stackGroup" key={copy} aria-hidden={copy ? true : undefined}>
                    {STACK_LOGOS.map((t) => (
                      <li className="stackItem" key={t.name} data-squish-host="">
                        <span className="stackLogo" data-squish=""><img src={t.img} alt="" loading="lazy" /></span>
                        <span className="stackName">{t.name}</span>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
            <div className="stackRow rev">
              <div className="stackTrack">
                {[0, 1].map((copy) => (
                  <ul className="stackGroup" key={copy} aria-hidden={copy ? true : undefined}>
                    {STACK_MORE.map((t) => (
                      <li className="stackPill" key={t} data-squish="0.8" data-gloss=""><ChipMark />{t}</li>
                    ))}
                  </ul>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ===== EXPERIENCE — its own section (the timeline + recognitions) ===== */
function ExperienceSection() {
  return (
    <section id="experience" className="expSection" aria-label="Experience">
      <div className="container">
        <SecTag n={3} label="Experience" />
        <h2 className="pageHead"><Words text={"Where I've\nbeen building."} /></h2>
        <div className="expReveal">
          <ExperienceList />
        </div>
      </div>
    </section>
  );
}

const EMAIL = "bharadwaj465@gmail.com";

function ContactSection() {
  const ctaRef = useMagnet<HTMLAnchorElement>(0.3);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef(0);
  const copy = (e: React.MouseEvent) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || !navigator.clipboard) return;
    e.preventDefault();
    navigator.clipboard.writeText(EMAIL).then(() => {
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
    }, () => { window.location.href = `mailto:${EMAIL}`; });
  };

  // a FAST cursor swipe near the sticker shoves it; it springs back elastically.
  // Decorative only (never a click target). Listener lives only while #contact is on screen.
  const secRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)").matches) return;
    const sec = secRef.current, inner = sec?.querySelector<HTMLElement>(".stickerInner");
    if (!sec || !inner) return;
    let last = { x: 0, y: 0, t: 0 }, raf = 0, ev: PointerEvent | null = null;
    const tick = () => {
      raf = 0;
      if (!ev) return;
      const now = performance.now();
      const speed = Math.hypot(ev.clientX - last.x, ev.clientY - last.y) / Math.max(now - last.t, 1);
      last = { x: ev.clientX, y: ev.clientY, t: now };
      const b = inner.getBoundingClientRect();
      const dx = b.left + b.width / 2 - ev.clientX, dy = b.top + b.height / 2 - ev.clientY, d = Math.hypot(dx, dy);
      if (speed < 1.2 || d > 150 || !d) return;
      const push = (1 - d / 150) * Math.min(speed * 18, 60);
      gsap.timeline()
        .to(inner, { x: (dx / d) * push, y: (dy / d) * push, rotation: Math.sign(dx || 1) * push * 0.4, duration: 0.15, ease: "power2.out", overwrite: true })
        .to(inner, { x: 0, y: 0, rotation: 0, duration: 1.2, ease: "elastic.out(1, 0.35)" });
    };
    const onMove = (e: PointerEvent) => { ev = e; if (!raf) raf = requestAnimationFrame(tick); };
    const seen = new IntersectionObserver(([e]) => {
      if (e?.isIntersecting) window.addEventListener("pointermove", onMove, { passive: true });
      else window.removeEventListener("pointermove", onMove);
    });
    seen.observe(sec);
    return () => { seen.disconnect(); window.removeEventListener("pointermove", onMove); cancelAnimationFrame(raf); };
  }, []);
  return (
    <section id="contact" className="contactSection" aria-label="Contact" ref={secRef}>
      <div className="contactInner">
        <SecTag n={4} label="Let's connect" />
        <h2 className="contactHead"><Words text={"Let's build something\nworth remembering."} /></h2>
        <a className="contactCta" ref={ctaRef} data-squish="0.7" data-gloss="" href={`mailto:${EMAIL}`} onClick={copy} data-copied={copied || undefined}
          title="Click to copy">
          <svg className="btnSkin" viewBox="0 0 320 64" preserveAspectRatio="none" aria-hidden="true">
            <path d="M16 32 C14 14 40 8 90 8 C170 7 250 7.5 300 10 C314 11 316 20 314 32 C316 46 300 56 248 56 C150 58 70 57 40 55 C20 54 18 44 16 32 Z" fill={INK} />
          </svg>
          <span className="ctaLabel">
            <span className="ctaEmail">{EMAIL}</span>
            <span className="ctaDone" aria-hidden="true">Copied
              <svg width="16" height="16" viewBox="0 0 22 22" fill="none"><path pathLength="100" d="M3 12 C5.5 13.5 7.5 16 9 18.5 C12 11.5 16 5.5 20 2.5" stroke="#fff" strokeWidth="2.6" strokeLinecap="round" /></svg>
            </span>
          </span>
        </a>
        <span className="srOnly" aria-live="polite">{copied ? "Email copied to clipboard" : ""}</span>
        <div className="contactSocials">
          {SOCIALS.filter((s) => s.img).map((s, i) => (
            <a key={s.label} className="socialPvc rvPop" data-squish-host="" href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
              data-rv="" ref={reveal} style={rvDelay(120 + i * 70)}>
              <img src={s.img} alt={s.label} loading="lazy" data-squish="" />
              <span className="socialPvcLabel">{s.label}</span>
            </a>
          ))}
        </div>
        {SOCIALS.some((s) => s.label !== "Email" && !s.img) && (
          <div className="contactLinks">
            {SOCIALS.filter((s) => s.label !== "Email" && !s.img).map((s) => (
              <a key={s.label} className="navlink" href={s.href} target={s.ext ? "_blank" : undefined} rel={s.ext ? "noopener noreferrer" : undefined}>{s.label}</a>
            ))}
          </div>
        )}
        <div className="contactFoot">
          <span className="thanksSticker rvPop" data-rv="" ref={reveal}>
            <span className="stickerTilt"><span className="stickerInner" data-squish="" data-gloss="">
              Thanks for scrolling!
              <svg width="26" height="26" viewBox="0 0 26 26" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                <circle cx="13" cy="13" r="11" /><path d="M8.5 15.5c2.4 2.6 6.6 2.6 9 0" /><circle cx="9.5" cy="10" r="0.6" fill="#fff" /><circle cx="16.5" cy="10" r="0.6" fill="#fff" />
              </svg>
            </span>
            </span>
          </span>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  APP                                                                */
/* ================================================================== */

/* ================================================================== */
/*  HOME PAGE — the YASH hero + the signature marshmallow-pour scroll    */
/*  transition that reveals a SELECTED WORK teaser (links to /work).     */
/* ================================================================== */

function HomePage({ onNavigate }: { onNavigate: (id: SectionId) => void }) {
  const [bombPos, setBombPos] = useState(() => Math.floor(Math.random() * BOMB_SPOTS.length));
  const [bombState, setBombState] = useState<"idle" | "burn" | "boom" | "hidden">("idle");
  const timers = useRef<number[]>([]);
  const reduced = useRef(false);

  /* ---------- hero entrance + interactions ---------- */
  useEffect(() => {
    reduced.current = !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced.current || window.location.hash === "#still" || window.location.hash.startsWith("#p=") || window.location.hash.startsWith("#dp=")) return;

    gsap.utils.toArray<SVGPathElement>(".drawPath").forEach((p, i) => {
      p.setAttribute("pathLength", "100");
      gsap.fromTo(p, { strokeDasharray: 100, strokeDashoffset: 100 }, { strokeDashoffset: 0, duration: 1, ease: "power2.inOut", delay: 1 + i * 0.05 });
    });
    gsap.from(".dotPop", { scale: 0, opacity: 0, duration: 0.6, ease: "power2.out", stagger: 0.05, delay: 1.5, transformOrigin: "center" });

    /* ---- the letters are little balloons: each is a physics body (springs) ----
       inflate in on load · float + sway out of phase at rest · lean toward the cursor
       · hover lifts · press squashes it onto its base (fattening into, and shoving,
       its neighbours), holding squeezes deeper · release hops it up and it rebounds
       past round. Only `transform` on each letter's composited layer is written. */
    const letters = Array.from(document.querySelectorAll<HTMLElement>(".heroChar"));
    const B = letters.map((el, i) => ({
      el, i, body: (el.firstElementChild as HTMLElement | null) ?? el,
      ix: spring(0.25), iy: spring(0.25),  // inflate (x/y on different springs → uneven, rubbery)
      p: spring(0), h: spring(0), y: spring(30), x: spring(0), r: spring(0),
      ox: 0.5, hold: 0, phase: i * 1.9,
    }));
    B.forEach((b) => { b.body.style.willChange = "transform"; b.el.style.opacity = "0"; });
    B.forEach((b) => {
      timers.current.push(window.setTimeout(() => {
        b.el.style.transition = "opacity .3s ease";
        b.el.style.opacity = "1";
        tune(b.ix, 1, 150, 9.5); // ζ≈0.39: a bouncy inflate with a little overshoot
        tune(b.iy, 1, 210, 10);
        tune(b.y, 0, 170, 15);
      }, 150 + b.i * 95));
    });

    // the wordmark wobbles like jelly when the page is flung (scroll velocity → stretch)
    const jellyEl = document.querySelector<HTMLElement>(".wmJelly");
    const jelly = spring(0, 170, 11);
    let lastY = window.scrollY, lastT = performance.now();
    const onScroll = () => {
      const now = performance.now(), dt = Math.max(now - lastT, 8);
      const v = ((window.scrollY - lastY) / dt) * 1000; // px/s
      lastY = window.scrollY; lastT = now;
      jelly.to = Math.max(-0.05, Math.min(0.05, v / 60000));
      wake(hero);
    };

    let t = 0, visible = true;
    const neighbourPush = (b: (typeof B)[number]) => {
      let push = 0;
      const L = B[b.i - 1], R = B[b.i + 1];
      if (L) push += Math.max(0, L.p.x) * 11;  // left neighbour fattening → pushes me right
      if (R) push -= Math.max(0, R.p.x) * 11;
      return push;
    };
    const render = (b: (typeof B)[number]) => {
      const by = Math.sin(t * 1.25 + b.phase) * 2.4;            // float on air
      const br = Math.sin(t * 0.85 + b.phase * 1.3) * 0.75;     // sway on the string
      const bs = Math.sin(t * 1.6 + b.phase) * 0.006;            // breathe
      const p = b.p.x, h = b.h.x;
      const sx = b.ix.x * (1 + 0.02 * h + 0.13 * p + bs), sy = b.iy.x * (1 + 0.02 * h - 0.17 * p - bs);
      const rot = b.r.x + (b.ox - 0.5) * 9 * Math.max(p, 0) + br;
      b.body.style.transform = `translate(${b.x.x.toFixed(2)}px, ${(b.y.x - 7 * h + by).toFixed(2)}px) rotate(${rot.toFixed(3)}deg) scale(${sx.toFixed(4)}, ${sy.toFixed(4)})`;
    };
    const hero: Mover = (dt) => {
      if (!visible) {
        // off-screen: finish every motion at its resting pose, then sleep
        B.forEach((b) => { snap(b.ix, b.iy, b.p, b.h, b.y, b.x, b.r); render(b); });
        jelly.to = 0; snap(jelly);
        if (jellyEl) jellyEl.style.transform = "";
        return false;
      }
      t += dt;
      B.forEach((b) => {
        b.x.to = neighbourPush(b);
        [b.ix, b.iy, b.p, b.h, b.y, b.x, b.r].forEach((s) => stepSpring(s, dt));
        render(b);
      });
      jelly.to *= 0.86; // the fling fades; the spring then wobbles it back to round
      stepSpring(jelly, dt);
      if (jellyEl) {
        const j = jelly.x;
        jellyEl.style.transform = `translateY(${(j * 150).toFixed(2)}px) scale(${(1 - Math.abs(j) * 0.45).toFixed(4)}, ${(1 + Math.abs(j)).toFixed(4)})`;
      }
      return true; // runs while the hero is on screen (the idle float needs every frame)
    };
    const seen = new IntersectionObserver(([e]) => {
      visible = !!e?.isIntersecting;
      wake(hero);
    });
    const home = document.getElementById("home");
    if (home) seen.observe(home);
    wake(hero);

    // lean toward the cursor, as if curious (only letters near it, max ~3°)
    const onMove = (e: PointerEvent) => {
      B.forEach((b) => {
        const r = b.el.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2), dy = e.clientY - (r.top + r.height / 2);
        const f = Math.max(0, 1 - Math.hypot(dx, dy) / 620);
        tune(b.r, Math.max(-3, Math.min(3, (dx / 220) * 3 * f)), 90, 12);
      });
    };
    const onHeroLeave = () => B.forEach((b) => tune(b.r, 0, 90, 12));

    let held: (typeof B)[number] | null = null;
    const releaseLetter = () => {
      const b = held;
      if (!b) return;
      held = null;
      clearTimeout(b.hold);
      const depth = Math.max(b.p.x, 0);
      if (depth > 1.1) navigator.vibrate?.(8);
      playBalloonPop([1, 1.12, 1.24, 0.9][b.i] ?? 1, 0.28 + 0.3 * Math.min(depth / 1.6, 1));
      tune(b.p, 0, 430, 9.5);   // underdamped: rebounds tall, wobbles, settles
      b.y.v -= 240 + 260 * depth; // and hops off the ground a little
      tune(b.y, 0, 260, 13);
    };
    const handlers: Array<[EventTarget, string, EventListener]> = [];
    const on = (el: EventTarget, evt: string, fn: EventListener) => { el.addEventListener(evt, fn); handlers.push([el, evt, fn]); };
    B.forEach((b) => {
      on(b.el, "pointerenter", ((e: PointerEvent) => { if (e.pointerType === "mouse") tune(b.h, 1, 260, 17); }) as EventListener);
      on(b.el, "pointerleave", () => { tune(b.h, 0, 220, 17); if (held === b) releaseLetter(); });
      on(b.el, "pointerdown", ((e: PointerEvent) => {
        if (e.button !== 0) return;
        releaseLetter();
        const r = b.el.getBoundingClientRect();
        b.ox = Math.min(Math.max((e.clientX - r.left) / r.width, 0), 1);
        tune(b.p, 1, 700, 32);
        b.hold = window.setTimeout(() => tune(b.p, 1.6, 45, 13), 160); // keep squeezing
        held = b;
      }) as EventListener);
    });
    on(window, "pointerup", releaseLetter);
    on(window, "pointercancel", releaseLetter);
    on(window, "scroll", onScroll);
    if (home) {
      on(home, "pointermove", onMove as EventListener);
      on(home, "pointerleave", onHeroLeave);
    }
    /* the inflatable props rock on their bases (out of phase), lean toward the cursor,
       and when a squeeze is let go they hop off the ground with a twisty wobble. The
       wrap gets this transform; the img keeps the squish (scale/rotate) and the wrap's
       CSS lean (`rotate`) + drop-in (`translate`) compose on top. */
    const props = Array.from(document.querySelectorAll<HTMLElement>(".heroPropWrap")).map((el, i) => ({
      el, phase: i * 2.3 + 0.7, y: spring(0, 220, 12), r: spring(0, 160, 7.5), lean: spring(0, 70, 11),
    }));
    const propMover: Mover = (dt) => {
      props.forEach((p) => {
        [p.y, p.r, p.lean].forEach((s) => stepSpring(s, dt));
        const rock = Math.sin(t * 1.1 + p.phase) * 1.6;
        p.el.style.transform = `translateY(${p.y.x.toFixed(2)}px) rotate(${(rock + p.r.x + p.lean.x).toFixed(3)}deg)`;
      });
      return visible;
    };
    const wakeProps = () => wake(propMover);
    wakeProps();
    const seenProps = new IntersectionObserver(([e]) => { if (e?.isIntersecting) wakeProps(); });
    if (home) seenProps.observe(home);
    props.forEach((p, i) => {
      on(p.el, "pointerup", () => {
        playBalloonPop(i ? 0.8 : 0.72, 0.45);         // bigger inflatables, lower pop
        p.y.v -= 520;                                 // hop
        p.r.v += (i % 2 ? -1 : 1) * 260;              // and twist as it leaves the ground
        wakeProps();
      });
    });
    const onPropLean = (e: PointerEvent) => props.forEach((p) => {
      const r = p.el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const f = Math.max(0, 1 - Math.abs(dx) / 700);
      p.lean.to = Math.max(-4, Math.min(4, (dx / 160) * 4 * f));
    });
    if (home) on(home, "pointermove", onPropLean as EventListener);
    const cleanupLetters = () => { seen.disconnect(); seenProps.disconnect(); movers.delete(hero); movers.delete(propMover); B.forEach((b) => clearTimeout(b.hold)); };

    document.querySelectorAll<SVGSVGElement>(".ixRedraw").forEach((svg) => {
      const fn = () => {
        svg.querySelectorAll<SVGPathElement>(".drawPath").forEach((p) => {
          gsap.fromTo(p, { strokeDashoffset: 100 }, { strokeDashoffset: 0, duration: 0.7, ease: "power2.inOut", overwrite: "auto" });
        });
        const runner = svg.querySelector<SVGCircleElement>(".runner");
        const track = svg.querySelector<SVGPathElement>(".drawPath");
        if (runner && track) {
          const L = track.getTotalLength();
          const proxy = { t: 0 };
          runner.style.opacity = "1";
          gsap.to(proxy, {
            t: 1, duration: 0.7, ease: "power2.inOut", overwrite: "auto",
            onUpdate: () => { const pt = track.getPointAtLength(L * proxy.t); runner.setAttribute("cx", String(pt.x)); runner.setAttribute("cy", String(pt.y)); },
            onComplete: () => { gsap.to(runner, { opacity: 0, duration: 0.3 }); },
          });
        }
      };
      on(svg, "mouseenter", fn);
    });

    document.querySelectorAll<SVGSVGElement>(".ixSpin").forEach((svg) => {
      const fn = () => gsap.fromTo(svg, { rotation: 0, scale: 1 }, { rotation: 180, scale: 1.25, duration: 0.8, ease: "back.out(1.8)", onComplete: () => gsap.to(svg, { scale: 1, rotation: 0, duration: 0.6, ease: "power2.inOut" }) });
      on(svg, "mouseenter", fn);
    });

    return () => {
      handlers.forEach(([el, evt, fn]) => el.removeEventListener(evt, fn));
      timers.current.forEach(clearTimeout);
      cleanupLetters();
    };
  }, []);

  /* scroll-away: the YA | SH door panels drift apart and tilt out while the wordmark
     sinks + shrinks a touch (the reference's "YASH letters separate" transition).
     Transform-only on composited layers, scrubbed so it tracks the scrollbar. */
  useEffect(() => {
    if (prefersReduced()) return;
    gsap.registerPlugin(ScrollTrigger);
    const ctx = gsap.context(() => {
      gsap.timeline({ scrollTrigger: { trigger: "#home", start: "top top", end: "bottom top", scrub: 0.6 } })
        .to(".doorLeft", { xPercent: -10, rotation: -5, ease: "none" }, 0)
        .to(".doorRight", { xPercent: 10, rotation: 5, ease: "none" }, 0)
        .to(".heroWordmark", { y: () => window.innerHeight * 0.16, scale: 0.9, ease: "none" }, 0)
        .to(".heroIntro", { y: () => window.innerHeight * 0.16, ease: "none" }, 0)
        .to(".heroIntro", { autoAlpha: 0, ease: "power1.in", duration: 0.45 }, 0);
    });
    return () => ctx.revert();
  }, []);

  /* bomb: hover → fuse burns → BOOM → respawns elsewhere */
  useEffect(() => {
    if (reduced.current) return;
    if (bombState === "burn") {
      // hover lights the fuse: the spark keeps crackling at the tip (loops) until you
      // CLICK to detonate — or move away to put it out. No auto-explode.
      const spark = document.querySelector<SVGGElement>("#bombSpark");
      if (spark) {
        gsap.fromTo(spark, { scale: 0, transformOrigin: "center" }, { scale: 1, duration: 0.18, ease: "power2.out" });
        gsap.to(spark, { rotation: 18, scale: 0.82, duration: 0.1, yoyo: true, repeat: -1, ease: "power1.inOut", transformOrigin: "center" });
      }
    }
    if (bombState === "boom") {
      playBombPop();
      gsap.fromTo("#blast", { scale: 0.2, rotation: -18, opacity: 1, transformOrigin: "center" }, { scale: 1, rotation: 0, duration: 0.45, ease: "back.out(2.4)" });
      timers.current.push(window.setTimeout(() => {
        gsap.to("#blast", { scale: 1.18, opacity: 0, duration: 0.4, ease: "power2.in", onComplete: () => setBombState("hidden") });
      }, 1000));
    }
    if (bombState === "hidden") {
      timers.current.push(window.setTimeout(() => { setBombPos((p) => randNext(BOMB_SPOTS.length, p)); setBombState("idle"); }, 1600));
    }
    if (bombState === "idle") {
      gsap.fromTo("#bombWrap", { scale: 0.5, opacity: 0, transformOrigin: "center bottom" }, { scale: 1, opacity: 1, duration: 0.6, ease: "back.out(1.8)" });
    }
    return () => {
      // stop the looping fuse spark whenever the bomb leaves the burn state
      const spark = document.querySelector("#bombSpark");
      if (spark) gsap.killTweensOf(spark);
    };
  }, [bombState]);

  return (
    <>
      {/* ====== HERO STAGE — the centred YASH wordmark; flows straight into About below ====== */}
      <div className="stage" id="home">
        {/* ---- HERO LAYER (the YASH hero; the A splits open like a door) ---- */}
        <div className="heroLayer">
          <main style={{ position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "clamp(132px, 17vh, 184px) 20px clamp(48px, 7vh, 80px)" }}>

            <h1 className="srOnly">Yash Bharadwaj, design engineer</h1>

            {/* ---- giant wordmark + living annotations ---- */}
            <div className="heroWordmark" style={{ position: "relative", width: "min(58vw, 820px, 88vh)", marginTop: 8 }}>


              <Doodle cls="ixRedraw shakeable" w={66} h={30} style={{ left: "23%", top: "4%", zIndex: 3 }}>
                <path className="drawPath" d="M4 26 Q33 7 62 22" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
                <path className="drawPath" d="M14 28 Q35 17 56 26" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
              </Doodle>
              <Doodle cls="ixRedraw shakeable" w={66} h={30} style={{ right: "14%", top: "2%", zIndex: 3 }}>
                <path className="drawPath" d="M62 26 Q33 7 4 22" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
                <path className="drawPath" d="M52 28 Q31 17 10 26" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
              </Doodle>

              <Doodle cls="ixSpin shakeable" w={40} h={40} style={{ right: "-5%", top: "-12%", zIndex: 3 }}>
                <path className="drawPath" d="M20 3 C21.5 13 23 16.5 33 18 C23 19.5 21.5 23 20 33 C18.5 23 17 19.5 7 18 C17 16.5 18.5 13 20 3 Z" stroke={BLUE} strokeWidth="2.6" strokeLinejoin="round" />
              </Doodle>

              {/* the wordmark — click a letter to stretch it */}
              <div className="wmJelly" style={{ position: "relative", zIndex: 2 }}>
                <Wordmark charClass="heroChar" />
              </div>

              {/* inflatable caution sign leaning on the Y's upper-left blob */}
              <span className="heroPropWrap heroCautionWrap" aria-hidden="true">
                <img className="heroProp" src={heroCaution} alt="" draggable={false} data-squish="0.9" />
              </span>


              {/* bomb — ignites on hover, explodes, respawns elsewhere */}
              {bombState !== "hidden" && (
                <div id="bombWrap" style={{ position: "absolute", zIndex: 4, width: 92, height: 92, cursor: "pointer", ...BOMB_SPOTS[bombPos] }}
                  onMouseEnter={() => bombState === "idle" && setBombState("burn")}
                  onMouseLeave={() => bombState === "burn" && setBombState("idle")}
                  onClick={() => (bombState === "idle" || bombState === "burn") && setBombState("boom")}>
                  {bombState !== "boom" ? (
                    <svg width="92" height="92" viewBox="0 0 92 92" fill="none" aria-hidden="true">
                      <rect x="0" y="0" width="92" height="92" fill="transparent" />
                      <circle cx="32" cy="62" r="21" fill={INK} />
                      <circle cx="25" cy="55" r="4.5" fill="#f6f4ee" opacity="0.9" />
                      <path d="M43 46 L51 38 L57 44 L49 52 Z" fill={INK} />
                      <path id="bombFuse" d="M53 41 C58 32 66 34 69 24" stroke={INK} strokeWidth="4" strokeLinecap="round" />
                      <g id="bombSpark" style={{ opacity: bombState === "burn" ? 1 : 0 }}>
                        <path d="M69 24 l6 -7 M72 28 l9 -2 M66 18 l0 -9 M76 20 l6 -5" stroke={INK} strokeWidth="2.6" strokeLinecap="round" />
                        <circle cx="80" cy="13" r="1.8" fill={INK} />
                        <circle cx="84" cy="22" r="1.6" fill={INK} />
                      </g>
                      {bombState === "idle" && (
                        <g opacity="0.85">
                          <path d="M70 22 l4 -5 M73 27 l7 -2" stroke={INK} strokeWidth="2.2" strokeLinecap="round" />
                          <circle cx="79" cy="14" r="1.5" fill={INK} />
                        </g>
                      )}
                    </svg>
                  ) : (
                    <svg id="blast" width="130" height="130" viewBox="0 0 130 130" style={{ position: "absolute", left: -19, top: -19 }} aria-hidden="true">
                      <path d={starPath(65, 65, 60, 28, 11)} fill={INK} />
                      <path d={starPath(65, 65, 34, 15, 9)} fill="#f6f4ee" />
                      <g stroke={INK} strokeWidth="2.6" strokeLinecap="round">
                        <path d="M65 2 l0 -2 M118 30 l6 -4 M124 88 l8 3 M30 122 l-5 7 M8 60 l-8 -2" />
                      </g>
                      <circle cx="14" cy="24" r="2.4" fill={INK} />
                      <circle cx="116" cy="112" r="2.6" fill={INK} />
                    </svg>
                  )}
                </div>
              )}

              {/* short blue cup hugging a letter's rounded bottom, with a little accent dot */}
              <Doodle cls="ixRedraw shakeable" w={82} h={24} style={{ left: "27%", bottom: "17%", zIndex: 3 }}>
                <path className="drawPath" d="M4 8 Q33 21 60 12" stroke={BLUE} strokeWidth="3.4" strokeLinecap="round" />
                <circle cx="73" cy="9" r="2.6" fill={BLUE} />
                <circle className="runner" r="4" fill={BLUE} opacity="0" />
              </Doodle>
              <Doodle cls="ixRedraw shakeable" w={82} h={24} style={{ right: "27%", bottom: "19%", zIndex: 3 }}>
                <path className="drawPath" d="M22 12 Q49 21 78 8" stroke={BLUE} strokeWidth="3.4" strokeLinecap="round" />
                <circle cx="8" cy="9" r="2.6" fill={BLUE} />
                <circle className="runner" r="4" fill={BLUE} opacity="0" />
              </Doodle>

            </div>

            {/* ---- a hand-written hello + a fanned hand of project polaroids (the work,
                 first thing after the name). Each card jumps to that project. ---- */}
            <div className="heroIntro">
              <p className="heroNote">
                hey, I'm Yash, a <U>design engineer</U> from Delhi.
                {/* inflatable stacking cone, set down just past "Delhi." (squeezable) */}
                <span className="heroPropWrap heroConeWrap" aria-hidden="true">
                  <img className="heroProp" src={heroCone} alt="" draggable={false} data-squish="0.9" />
                </span>
              </p>
              <div className="heroFan" role="list" aria-label="Selected work">
                {HERO_FAN.map((p, i) => (
                  // the slot holds the card's angle in the hand (one shared pivot far below);
                  // the card inside keeps its own squish + "pull it out of the hand" lift
                  <div key={p.t} role="listitem" className="fanSlot"
                    style={{ "--i": i, "--off": i - (HERO_FAN.length - 1) / 2 } as React.CSSProperties}>
                    <a className="fanCard" data-squish="0.5" href="#work"
                      onClick={(e) => { e.preventDefault(); showWork(p.tab); onNavigate("work"); }}>
                      <span className={`fanShot${p.fit === "contain" ? " contain" : ""}`}><img src={p.img} alt="" loading="eager" /></span>
                      <span className="fanCap">{p.t}</span>
                    </a>
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
        {/* the navbar lives OUTSIDE the page as a persistent fixed layer (<SiteNav /> in App) */}
      </div>
    </>
  );
}

/* ================================================================== */
/*  PROJECTS SECTION — lives on the home page (#work). A toggle flips    */
/*  Mobile App and Website (Website = Projects + Components). Each item   */
/*  is an inflated PVC card with media; contained to the 1140 column.    */
/* ================================================================== */

/* tiny blue spark used as the tech-chip mark */
function ChipMark() {
  return (
    <svg className="pjChipMark" width="9" height="9" viewBox="0 0 9 9" fill="none" aria-hidden="true">
      <path d="M4.5 0 L5.55 3.45 L9 4.5 L5.55 5.55 L4.5 9 L3.45 5.55 L0 4.5 L3.45 3.45 Z" fill={BLUE} />
    </svg>
  );
}

/* full project = media panel (left) + number · status · title · description · tech chips (right) */
/* pointer position inside the media → CSS vars the cursor pill follows. Clamped so the
   pill never clips at the rounded edges (ponytail: 56/28 = half the pill + a margin;
   update if its padding/font changes), and re-placed on scroll since that moves the
   media under a still pointer without any pointer event. */
let lastPt = { x: 0, y: 0 };
const placeCursor = (el: HTMLElement) => {
  const r = el.getBoundingClientRect();
  el.style.setProperty("--cx", `${Math.min(Math.max(lastPt.x - r.left, 56), r.width - 56)}px`);
  el.style.setProperty("--cy", `${Math.min(Math.max(lastPt.y - r.top, 28), r.height - 28)}px`);
};
const trackCursor = (e: React.PointerEvent<HTMLElement>) => {
  lastPt = { x: e.clientX, y: e.clientY };
  placeCursor(e.currentTarget);
};
// on enter, jump straight to the pointer (no streak in from the last exit point)
const snapCursor = (e: React.PointerEvent<HTMLElement>) => {
  const el = e.currentTarget;
  el.dataset.snap = "";
  trackCursor(e);
  void el.offsetWidth; // flush styles so the jump isn't transitioned
  delete el.dataset.snap;
};
if (typeof window !== "undefined") {
  window.addEventListener("scroll", () => {
    const el = document.querySelector<HTMLElement>(".pjMedia.isLink:hover");
    if (el) placeCursor(el);
  }, { passive: true });
}

function ProjectRow({ p, n }: { p: Proj; n: number }) {
  const initials = p.t.split(" ").map((w) => w[0]).join("").slice(0, 2);
  const mediaInner = p.img
    ? <img className={p.fit === "contain" ? "pjContain" : undefined} src={p.img} alt={p.t} loading="lazy" />
    : <div className="pjMediaPh"><span>{initials}</span></div>;
  return (
    <article className="pjRow" data-rv="" ref={reveal}>
      {p.l ? (
        // mouse shortcut to the same link as "Visit project" below, so it's kept out of
        // the tab order / a11y tree (the text link is the accessible one)
        <a className="pjMedia isLink" data-squish="0.18" href={p.l} target="_blank" rel="noopener noreferrer" tabIndex={-1} aria-hidden="true"
          onPointerEnter={snapCursor} onPointerMove={trackCursor}>
          {mediaInner}
          <span className="pjCursor">Visit <ArrowIcon /></span>
        </a>
      ) : <div className="pjMedia">{mediaInner}</div>}
      <div className="pjInfo">
        <h2 className="pjTitle">{p.t}</h2>
        <p className="pjDesc">{p.d}</p>
        {p.stack && (
          <div className="pjStack">
            {p.stack.map((s) => <span className="pjChip" key={s} data-squish="0.8" data-gloss=""><ChipMark />{s}</span>)}
          </div>
        )}
        {p.l && (
          <a className="pjLink" href={p.l} target="_blank" rel="noopener noreferrer">Visit project <ArrowIcon /></a>
        )}
      </div>
    </article>
  );
}

/* compact card — used only for the small Website "Components". Whole card is a link
   to the live demo when one exists. */
function ProjCard({ p, i }: { p: Proj; i: number }) {
  const initials = p.t.split(" ").map((w) => w[0]).join("").slice(0, 2);
  const inner = (
    <>
      <div className="projCardMedia square">
        {p.img ? <img src={p.img} alt={p.t} loading="lazy" /> : <div className="projCardPh"><span>{initials}</span></div>}
      </div>
      <div className="projCardBody">
        <span className="projCardTitle">{p.t}{p.l && <ArrowIcon />}</span>
        {p.d && <span className="projCardDesc">{p.d}</span>}
        {p.stack && <span className="projCardTags">{p.stack.join(" · ")}</span>}
      </div>
    </>
  );
  return p.l
    ? <a className="projCard isStatic isLink" data-squish="0.25" href={p.l} target="_blank" rel="noopener noreferrer" data-rv="" ref={reveal} style={rvDelay(i * 80)}>{inner}</a>
    : <div className="projCard isStatic" data-squish="0.25" data-rv="" ref={reveal} style={rvDelay(i * 80)}>{inner}</div>;
}

/* PVC jiggle wave for the Mobile App ⇄ Website thumb — the dark pill is sliced
   into WAVE_SEGS vertical strips; the strip at the wall it just hit wobbles first
   and hardest, each strip toward the far side starts later (WAVE_STEP apart) and
   smaller (WAVE_DECAY falloff), so a single ripple travels across and fades. */
const WAVE_SEGS = 8;     // vertical slices the pill is built from
const WAVE_AMP = 0.4;    // crest height at the impact edge (scaleY delta)
const WAVE_DECAY = 0.78; // amplitude falloff per slice toward the far edge
const WAVE_STEP = 28;    // ms of delay between adjacent slices → wave travel speed
const WAVE_TOUCH = 230;  // ms before the crest starts (≈ when the pill meets the wall)

function WorkPage() {
  const [tab, setTab] = useState<"mobile" | "web">("mobile");     // selected (drives the thumb)
  const [shown, setShown] = useState<"mobile" | "web">("mobile"); // currently rendered section
  const [leaving, setLeaving] = useState(false);                  // exit animation in flight
  // the thumb is a spring body: x 0 (Mobile) → 1 (Website). It stretches along its
  // velocity while it travels and squashes flat against the wall when it overshoots.
  const thumbRef = useRef<HTMLSpanElement>(null);
  const skinRef = useRef<HTMLSpanElement>(null);
  const thumbX = useRef(spring(0, 300, 19));  // ζ≈0.55: one firm bump, a little wobble
  const thumbMover = useRef<Mover | null>(null);
  useEffect(() => {
    const sx = thumbX.current;
    const render = () => {
      const t = thumbRef.current, k = skinRef.current;
      if (!t || !k) return;
      const x = sx.x;
      const over = x > 1 ? x - 1 : x < 0 ? -x : 0;        // past a wall → compress into it
      const stretch = over ? 0 : Math.min(Math.abs(sx.v) * 0.035, 0.18);
      const squash = Math.min(over * 1.8, 0.15);
      t.style.transform = `translateX(${(Math.min(Math.max(x, 0), 1) * 100).toFixed(2)}%)`;
      k.style.transformOrigin = x > 1 ? "right center" : x < 0 ? "left center" : "center";
      k.style.transform = `scale(${(1 + stretch - squash).toFixed(4)}, ${(1 - stretch * 0.45 + squash * 0.6).toFixed(4)})`;
    };
    thumbMover.current = (dt) => { stepSpring(sx, dt); render(); return !atRest(sx); };
    render();
  }, []);
  useEffect(() => {
    const sx = thumbX.current;
    sx.to = tab === "web" ? 1 : 0;
    if (prefersReduced()) { snap(sx); thumbMover.current?.(0); return; }
    if (thumbMover.current) wake(thumbMover.current);
  }, [tab]);

  // switching choreographs over ~0.39s: current section fades OUT (0.13s),
  // then the next section fades/settles IN (0.26s). Re-keyed to restart the CSS animation.
  const pick = (t: "mobile" | "web") => {
    if (t === tab) return; // mid-exit clicks land: onStageAnimEnd swaps to the latest tab
    setTab(t);
    // reduced motion → no keyframes fire (no animationend), so swap immediately
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) { setShown(t); return; }
    setLeaving(true);
  };
  // a hero polaroid can ask for its tab (mobile/web) before scrolling here
  const pickRef = useRef(pick);
  pickRef.current = pick;
  useEffect(() => {
    const on = (e: Event) => pickRef.current((e as CustomEvent<"mobile" | "web">).detail);
    window.addEventListener("worktab", on);
    return () => window.removeEventListener("worktab", on);
  }, []);
  const onStageAnimEnd = (e: React.AnimationEvent) => {
    if (e.target !== e.currentTarget) return; // ignore bubbled child animations
    if (leaving) { setShown(tab); setLeaving(false); }
  };

  const mobileSection = (
    <div className="projSection">
      <div className="pjList">
        {MOBILE_APPS.map((p, i) => <ProjectRow key={p.t} p={p} n={i + 1} />)}
      </div>
    </div>
  );
  const webSection = (
    <>
      <div className="projSection">
        <div className="pjList">
          {WEB_PROJECTS.map((p, i) => <ProjectRow key={p.t} p={p} n={i + 1} />)}
        </div>
      </div>
      <div className="projSection">
        <h2 className="projGroupTitle">Components</h2>
        <div className="projGrid g4">
          {WEB_COMPONENTS.map((p, i) => <ProjCard key={p.t} p={p} i={i} />)}
        </div>
      </div>
    </>
  );

  return (
    <section className="workPage" id="work" aria-label="Projects">
      <div className="container">
        <SecTag n={1} label="Selected work" />
        <h2 className="pageHead"><Words text={"Things I've designed\n& built."} /></h2>
        <p className="pageLede" data-rv="" ref={reveal} style={rvDelay(200)}>
          A mix of shipped mobile apps, websites and the little interface components I build
          along the way. Flip between the two below.
        </p>

        {/* toggle — Mobile App ⇄ Website */}
        <div className="projToggle" role="tablist" aria-label="Project category" data-rv="" ref={reveal} style={rvDelay(280)}>
          <span className="projToggleThumb" ref={thumbRef}>
            {/* the dark pill: transform written per frame by the spring above */}
            <span className="projToggleThumbSkin" ref={skinRef} />
          </span>
          <button className={`projTab${tab === "mobile" ? " on" : ""}`} role="tab" aria-selected={tab === "mobile"} onClick={() => pick("mobile")}>Mobile App</button>
          <button className={`projTab${tab === "web" ? " on" : ""}`} role="tab" aria-selected={tab === "web"} onClick={() => pick("web")}>Website</button>
        </div>

        <div
          key={shown}
          className={`projStage${leaving ? " leaving" : ""}`}
          onAnimationEnd={onStageAnimEnd}
        >
          {shown === "mobile" ? mobileSection : webSection}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  APP — route shell. Persistent nav + quick-menu wrap a swapped page:  */
/*  / (home) · /work · /about. Contact is a shared footer on every page. */
/* ================================================================== */

/* tiny History-API router — no dependency; deep links + back/forward work
   because the server serves index.html for every path (catch-all route). */
function usePath(): [string, (to: string) => void] {
  const [path, setPath] = useState(() => (typeof window !== "undefined" ? window.location.pathname : "/"));
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const navigate = (to: string) => {
    // different page → let the marshmallow curtain reset scroll under cover (no
    // pre-transition jump); same page → just hop to the top.
    if (to !== window.location.pathname) { window.history.pushState({}, "", to); setPath(to); }
    else window.scrollTo(0, 0);
  };
  return [path, navigate];
}

export function App() {
  const [menuOpen, setMenuOpen] = useState(() => typeof window !== "undefined" && window.location.hash === "#menu");
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState<SectionId>("home");

  // every nav/menu click smooth-scrolls to a section on the one page (no route change)
  const go = (id: SectionId) => {
    setMenuOpen(false);
    document.body.style.overflow = "";
    if (id === "home") {
      window.scrollTo({ top: 0 }); // html { scroll-behavior } decides: smooth, or instant under reduced motion
      history.replaceState(null, "", window.location.pathname);
      return;
    }
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ block: "start" });
      history.replaceState(null, "", `#${id}`);
    }
  };

  // balloon squish on every [data-squish] object + cursor-following gloss
  useEffect(() => {
    const offSquish = initSquish(), offGloss = initGloss();
    return () => { offSquish(); offGloss(); };
  }, []);

  // solid cream background, no grid
  useEffect(() => {
    document.body.style.backgroundColor = "#efece4";
    document.body.style.backgroundImage = "none";
  }, []);

  // deter casual image saving — block the right-click menu and drag on images
  // across the whole site (Save / Copy / Open image as…). A deterrent only:
  // determined users can still reach assets via devtools/network.
  useEffect(() => {
    const isImg = (t: EventTarget | null) =>
      t instanceof HTMLElement && (t.tagName === "IMG" || !!t.closest("img"));
    const onCtx = (e: MouseEvent) => { if (isImg(e.target)) e.preventDefault(); };
    const onDrag = (e: DragEvent) => { if (isImg(e.target)) e.preventDefault(); };
    document.addEventListener("contextmenu", onCtx);
    document.addEventListener("dragstart", onDrag);
    return () => {
      document.removeEventListener("contextmenu", onCtx);
      document.removeEventListener("dragstart", onDrag);
    };
  }, []);

  // ⌘K / Ctrl-K toggles the quick-menu (a power-user bonus) · Esc closes it
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) { e.preventDefault(); setMenuOpen((v) => !v); }
      else if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // lock page scroll while the full-screen mobile menu is open
  useEffect(() => { document.body.style.overflow = menuOpen ? "hidden" : ""; }, [menuOpen]);

  // SCROLL — sticky-shrink the nav + SCROLLSPY (highlight the section you're in).
  // Uses viewport-relative tops so it's robust to the pinned hero. React de-dupes
  // same-value setState, so this stays cheap despite firing every scroll frame.
  useEffect(() => {
    const ids: SectionId[] = ["work", "about", "experience", "contact"];
    const onScroll = () => {
      setScrolled(window.scrollY > 60);
      const line = window.innerHeight * 0.32;
      let cur: SectionId = "home";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) cur = id;
      }
      setActive(cur);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // deep link: /#about (etc.) or the old /work, /about paths land on the right section
  useEffect(() => {
    const hash = window.location.hash.replace("#", "");
    const fromPath = window.location.pathname.replace("/", ""); // "work" | "about" | ""
    const id = ["about", "experience", "work", "contact"].includes(hash) ? hash
      : ["about", "work"].includes(fromPath) ? fromPath : "";
    if (!id) return;
    // wait for fonts (capped at 1.5s) so the target doesn't reflow, then jump instantly (a
    // smooth scroll gets cancelled by ScrollTrigger's load refresh). Skipped if the visitor
    // already started scrolling, so a slow connection never yanks them.
    let cancelled = false;
    const stop = () => { cancelled = true; };
    const evts = ["wheel", "touchstart", "keydown"] as const;
    evts.forEach((t) => window.addEventListener(t, stop, { once: true, passive: true }));
    const start = window.scrollY;
    const t = window.setTimeout(() => {
      void Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 1500))]).then(() => {
        if (!cancelled && Math.abs(window.scrollY - start) < 4) document.getElementById(id)?.scrollIntoView({ block: "start", behavior: "instant" });
      });
    }, 420);
    return () => { clearTimeout(t); evts.forEach((t) => window.removeEventListener(t, stop)); };
  }, []);

  return (
    <>
      <GlobalDefs />

      <SiteNav active={active} scrolled={scrolled} onNavigate={go} onOpenMenu={() => setMenuOpen(true)} />

      {/* ONE PAGE — Hero → Work → About → Experience → Contact, every section reachable
          by a short scroll or one click from the always-visible nav. */}
      <HomePage onNavigate={go} />
      <WorkPage />
      <AboutSection />
      <ExperienceSection />
      <ContactSection />

      {/* full-screen menu — the mobile navigation (and a ⌘K bonus on desktop) */}
      <QuickMenu open={menuOpen} active={active} onClose={() => setMenuOpen(false)} onNavigate={go} />
    </>
  );
}

export default App;
